import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { track } from "@/lib/track";
import { Lock, CheckCircle2, ArrowLeft, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import {
  STEPS, DIMENSIONS, APTITUDE, APTITUDE_LEGEND, INTERVIEW, PERSONAS,
  CAREER_EXPLORATION, ACK_TEXT, INTRO_NOTE,
} from "@/data/assessmentForm";
import { Field, Likert } from "@/components/AssessmentFields";
import { SmartTip } from "@/components/SmartTip";
import { IntakeHelpAssistant } from "@/components/IntakeHelpAssistant";
import { AccountGateway } from "@/components/AccountGateway";
import { useClientAuth } from "@/context/ClientAuth";
import { SlotPicker, SessionScheduler } from "@/components/SlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIM_SHORT = {
  analytical: "Analytical", creative: "Creative", people: "People",
  business: "Business", technology: "Technology", organization: "Organization",
};

const Scorecard = ({ scores, email, booking, token, onBooked }) => {
  const data = DIMENSIONS.map((d) => ({ dim: DIM_SHORT[d.key], score: scores[d.key] || 0 }));
  const ranked = [...DIMENSIONS].sort((a, b) => (scores[b.key] || 0) - (scores[a.key] || 0));
  return (
    <motion.div data-testid="scorecard" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="max-w-4xl mx-auto px-6 py-24">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Assessment complete</p>
      <h1 className="font-head text-4xl sm:text-5xl font-black tracking-tight mt-3">Your Career Scorecard</h1>
      <p className="text-[#3A4A63] mt-4 max-w-xl">
        A copy of this scorecard has been emailed to <strong>{email}</strong>. Rhea will review your full
        responses before your session.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mt-10">
        <div className="bg-white p-6 h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={data} outerRadius="70%">
              <PolarGrid stroke="#E5E7EB" />
              <PolarAngleAxis dataKey="dim" tick={{ fill: "#041C3E", fontSize: 12, fontWeight: 700 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar dataKey="score" stroke="#D94833" fill="#D94833" fillOpacity={0.25} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white p-6 flex flex-col justify-center gap-4">
          {ranked.map((d, i) => (
            <div key={d.key} data-testid={`score-row-${d.key}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold flex items-center gap-2">
                  {i === 0 && <CheckCircle2 size={14} className="text-[#D94833]" />}
                  {d.label}
                </span>
                <span className="font-head font-bold">{scores[d.key] || 0}%</span>
              </div>
              <div className="h-1.5 bg-[#F3F4F6]">
                <motion.div className={`h-full ${i === 0 ? "bg-[#D94833]" : "bg-[#041C3E]"}`}
                  initial={{ width: 0 }} animate={{ width: `${scores[d.key] || 0}%` }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.08, ease: [0.16, 1, 0.3, 1] }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10" data-testid="scorecard-booking">
        <SessionScheduler token={token} heading="Your sessions with Rhea" />
      </div>
      <Link data-testid="scorecard-home-link" to="/"
        className="inline-block mt-10 border border-[#041C3E] text-[#041C3E] px-8 py-4 font-semibold tracking-wide hover:bg-[#041C3E] hover:text-white transition-colors duration-300">
        Back to home
      </Link>
    </motion.div>
  );
};

const Confirmation = ({ info, token, onBooked, onSkip }) => (
  <motion.div data-testid="payment-confirmation" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl mx-auto px-6 py-24">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">
      <CheckCircle2 size={14} /> Payment confirmed
    </div>
    <h1 className="font-head text-4xl sm:text-5xl font-black tracking-tight mt-3">
      Welcome, {info.name.split(" ")[0]}. Let's lock in your session.
    </h1>
    <p className="text-[#3A4A63] mt-4 max-w-xl">
      Your <strong>{info.tier_name}</strong> is unlocked. Choose a time with Rhea. A Google Calendar invite
      with a Meet link goes to <strong>{info.email}</strong>, then complete your Client Intake and Career
      Discovery Assessment.
    </p>
    <div className="mt-10">
      <SlotPicker token={token} onBooked={onBooked} onSkip={onSkip} />
    </div>
  </motion.div>
);

const levelFor = (t) => (t >= 25 ? "High" : t >= 19 ? "Medium" : t >= 13 ? "Emerging" : "Lower");

export default function Intake() {
  const { token } = useParams();
  const { user: clientUser } = useClientAuth();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [booking, setBooking] = useState(null);
  const [scores, setScores] = useState(null);
  const [busy, setBusy] = useState(false);

  // wizard state
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({ ack_date: new Date().toISOString().slice(0, 10) });
  const [personas, setPersonas] = useState([]);
  const [aptitude, setAptitude] = useState(Array(36).fill(null));
  const [interview, setInterview] = useState(Array(9).fill(null));
  const [acknowledged, setAcknowledged] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const hydrated = useRef(false);

  const setValue = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  useEffect(() => {
    axios.get(`${API}/intake/${token}`)
      .then((res) => {
        setInfo(res.data);
        setBooking(res.data.booking);
        const d = res.data.draft;
        if (d && !res.data.submitted) {
          if (d.values) setValues({ ...d.values, full_name: res.data.name, email: res.data.email, ack_name: res.data.name });
          else setValues((v) => ({ ...v, full_name: res.data.name, email: res.data.email, ack_name: res.data.name }));
          if (Array.isArray(d.personas)) setPersonas(d.personas);
          if (Array.isArray(d.aptitude) && d.aptitude.length === 36) setAptitude(d.aptitude);
          if (Array.isArray(d.interview) && d.interview.length === 9) setInterview(d.interview);
          if (typeof d.acknowledged === "boolean") setAcknowledged(d.acknowledged);
          if (typeof d.stepIdx === "number") setStepIdx(d.stepIdx);
        } else {
          setValues((v) => ({ ...v, full_name: res.data.name, email: res.data.email, ack_name: res.data.name }));
        }
        if (res.data.submitted && res.data.scores) {
          setScores(res.data.scores);
          setState("done");
        } else {
          setState(res.data.skip_booking || res.data.booking ? "ready" : "confirm");
        }
      })
      .catch(() => setState("locked"));
  }, [token]);

  // Autosave progress (debounced) once the assessment wizard is active
  useEffect(() => {
    if (state !== "ready") return;
    if (!hydrated.current) { hydrated.current = true; return; }
    setSaveStatus("saving");
    const t = setTimeout(() => {
      axios.post(`${API}/intake/${token}/draft`, {
        data: { values, personas, aptitude, interview, acknowledged, stepIdx },
      })
        .then(() => setSaveStatus("saved"))
        .catch(() => setSaveStatus("idle"));
    }, 900);
    return () => clearTimeout(t);
  }, [values, personas, aptitude, interview, acknowledged, stepIdx, state, token]);

  const steps = useMemo(() => STEPS.filter((s) => !s.persona || personas.includes(s.persona)), [personas]);
  const openedRef = useRef(false);
  useEffect(() => {
    if (state === "ready" && !openedRef.current) {
      openedRef.current = true;
      track("Intake Form", "Intake Opened", { user_email: info?.email });
      track("Intake Form", "Started Step: profile");
    }
  }, [state, info]);
  const step = steps[stepIdx];
  const progress = Math.round(((stepIdx + 1) / steps.length) * 100);

  const totals = useMemo(
    () => DIMENSIONS.map((_, i) => aptitude.slice(i * 6, (i + 1) * 6).reduce((a, b) => a + (b || 0), 0)),
    [aptitude]
  );
  const rankedDims = useMemo(
    () => DIMENSIONS.map((d, i) => ({ ...d, total: totals[i] })).sort((a, b) => b.total - a.total),
    [totals]
  );

  useEffect(() => { if (state === "ready") window.scrollTo({ top: 0, behavior: "smooth" }); }, [stepIdx, state]);
  useEffect(() => {
    if (aptitude.every((a) => a) && !values.r_top2) {
      setValue("r_top2", `${rankedDims[0].label} and ${rankedDims[1].label}`);
    }
  }, [aptitude]); // eslint-disable-line react-hooks/exhaustive-deps

  const REQ_MSG = "This field is required to tailor your session";

  const fieldRequired = (f) => {
    if (f.requiredIf) {
      const src = values[f.requiredIf.field];
      if (f.requiredIf.includes) return Array.isArray(src) && src.includes(f.requiredIf.includes);
      if (f.requiredIf.notEmpty) return Array.isArray(src) ? src.length > 0 : Boolean(src);
      return false;
    }
    return !!f.required;
  };

  const fieldEmpty = (f) => {
    const v = values[f.name];
    if (f.type === "checks") return !Array.isArray(v) || v.length === 0;
    if (f.type === "scale10") return !v;
    return !String(v ?? "").trim();
  };

  const validateStep = () => {
    if (step.personaSelect && personas.length === 0) {
      toast.error("Please choose at least one that describes you."); return false;
    }
    if (step.interview && !interview.every((n) => n)) {
      toast.error(`${REQ_MSG}. Please rate every statement.`); return false;
    }
    if (step.aptitude && !aptitude.every((a) => a)) {
      toast.error(`${REQ_MSG}. Please rate all 36 statements.`); return false;
    }
    for (const f of step.fields || []) {
      if (fieldRequired(f) && fieldEmpty(f)) {
        toast.error(`${f.label}: ${REQ_MSG}`); return false;
      }
    }
    if (step.acknowledgement && !acknowledged) {
      toast.error("Please accept the acknowledgment to submit."); return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    track("Intake Form", `Completed Step: ${step.id}`);
    if (stepIdx < steps.length - 1) {
      track("Intake Form", `Started Step: ${steps[stepIdx + 1].id}`);
      setStepIdx((i) => i + 1);
    }
    else submit();
  };
  const back = () => stepIdx > 0 && setStepIdx((i) => i - 1);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        persona: personas[0] || "student",
        profile: { ...values, personas, interview_ratings: interview, acknowledged, signature: values.ack_signature || "" },
        answers: aptitude.map((a) => a || 0),
      };
      const res = await axios.post(`${API}/intake/${token}/submit`, payload);
      track("Intake Form", "Form Submitted", { user_email: info?.email || values.email });
      setScores(res.data.scores);
      // Post-submission account creation gateway (skip if already signed in or invite flow)
      setState(clientUser || info?.is_invite ? "done" : "account");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="intake-loading">
        <p className="text-sm uppercase tracking-[0.2em] text-[#9CA3AF]">Unlocking assessment…</p>
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="intake-locked">
        <div className="max-w-md border border-[#041C3E] p-10">
          <Lock size={24} className="text-[#D94833]" />
          <h1 className="font-head text-3xl font-black mt-4">This assessment is locked.</h1>
          <p className="text-[#3A4A63] mt-3 text-sm leading-relaxed">
            The Client Intake and Career Discovery Assessment unlocks after a successful booking.
            Choose a program to get your secure access link.
          </p>
          <Link data-testid="intake-locked-home-link" to="/"
            className="inline-block mt-6 bg-[#D94833] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors duration-300">
            View programs
          </Link>
        </div>
      </div>
    );
  }

  if (state === "done" && scores) {
    return <Scorecard scores={scores} email={info.email || values.email} booking={booking} token={token} onBooked={setBooking} />;
  }

  if (state === "account" && scores) {
    return (
      <>
        <AccountGateway token={token} email={info.email || values.email} name={values.full_name}
          onDone={() => { setState("done"); window.scrollTo(0, 0); }}
          onSkip={() => { setState("done"); window.scrollTo(0, 0); }} />
        <IntakeHelpAssistant />
      </>
    );
  }

  if (state === "confirm") {
    return (
      <>
        <Confirmation info={info} token={token}
          onBooked={(b) => { setBooking(b); setState("ready"); window.scrollTo(0, 0); }}
          onSkip={() => { setState("ready"); window.scrollTo(0, 0); }} />
        <IntakeHelpAssistant />
      </>
    );
  }

  // state === "ready": comprehensive assessment wizard
  return (
    <div className="min-h-screen bg-[#F9F6F1]" data-testid="intake-form">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-head font-black text-sm sm:text-base tracking-tight text-[#041C3E]">
            The Next Move<span className="text-[#D94833]"> by Rhellex</span>
          </p>
          <span className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.15em] text-[#9CA3AF]" data-testid="intake-step-count">
              {info.tier_name} · Step {stepIdx + 1} of {steps.length}
            </span>
            <span data-testid="intake-save-status"
              className={`text-xs font-semibold tracking-wide transition-opacity duration-300 ${saveStatus === "idle" ? "opacity-0" : "opacity-100"} ${saveStatus === "saving" ? "text-[#9CA3AF]" : "text-[#0A7C4A]"}`}>
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Progress saved" : ""}
            </span>
          </span>
        </div>
        <div className="h-1 bg-[#F3F4F6]">
          <motion.div className="h-1 bg-[#D94833]" animate={{ width: `${progress}%` }} data-testid="intake-progress-bar" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        {stepIdx === 0 && (
          <div className="mb-8" data-testid="intake-intro">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Client intake and career discovery</p>
            <h1 className="font-head text-3xl sm:text-4xl font-black mt-3 text-[#041C3E]">
              {info.name ? `Welcome, ${info.name.split(" ")[0]}.` : "Welcome."}
            </h1>
            <p className="text-sm text-[#3A4A63] mt-3 leading-relaxed border-l-4 border-[#D94833] bg-white px-4 py-3">
              {INTRO_NOTE}
            </p>
            <p className="text-xs text-[#9CA3AF] mt-3" data-testid="intake-autosave-note">
              Your progress saves automatically. You can close this page and return anytime using the same link to pick up where you left off.
            </p>
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833] flex items-center">
              {step.title}{step.tip && <SmartTip text={step.tip} name={`step-${step.id}`} />}
            </p>
            {step.subtitle && <p className="text-base text-[#3A4A63] mt-2">{step.subtitle}</p>}

            <div className="mt-6 space-y-5">
              {step.personaSelect && (
                <div className="space-y-3" data-testid="persona-select">
                  {PERSONAS.map((p) => {
                    const on = personas.includes(p.key);
                    return (
                      <button type="button" key={p.key} data-testid={`persona-${p.key}`}
                        onClick={() => setPersonas((v) => on ? v.filter((x) => x !== p.key) : [...v, p.key])}
                        className={`w-full text-left p-5 border transition-colors ${
                          on ? "border-[#041C3E] bg-[#041C3E] text-white" : "border-[#E5E7EB] bg-white hover:border-[#041C3E]"
                        }`}>
                        <div className="flex items-center gap-3">
                          <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${on ? "border-white" : "border-[#9CA3AF]"}`}>
                            {on && <CheckCircle2 size={14} />}
                          </span>
                          <span className="font-semibold">{p.label}</span>
                        </div>
                        <p className={`text-sm mt-2 ml-8 ${on ? "text-white/70" : "text-[#9CA3AF]"}`}>{p.hint}</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {step.interview && (
                <Likert statements={INTERVIEW} values={interview} testPrefix="interview"
                  onChange={(i, n) => setInterview((v) => { const c = [...v]; c[i] = n; return c; })} />
              )}

              {step.aptitude && (
                <div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#9CA3AF] mb-5">
                    {APTITUDE_LEGEND.map((l) => <span key={l}>{l}</span>)}
                  </div>
                  {DIMENSIONS.map((dim, di) => (
                    <div key={dim.key} className="mb-6">
                      <p className="text-sm font-bold text-[#041C3E] mb-3">{dim.label}</p>
                      <Likert statements={APTITUDE.slice(di * 6, di * 6 + 6)}
                        values={aptitude.slice(di * 6, di * 6 + 6)} testPrefix={`apt-${dim.key}`}
                        onChange={(i, n) => setAptitude((v) => { const c = [...v]; c[di * 6 + i] = n; return c; })} />
                    </div>
                  ))}
                </div>
              )}

              {step.reflection && (
                <div className="bg-white border border-[#E5E7EB] p-5 mb-2" data-testid="reflection-map">
                  <p className="text-sm font-bold text-[#041C3E]">Career exploration map</p>
                  <div className="mt-3 space-y-3">
                    {rankedDims.slice(0, 3).map((d) => {
                      const map = CAREER_EXPLORATION.find((c) => c.dim === d.label);
                      return (
                        <div key={d.key} className="text-sm border-l-4 border-[#D94833] pl-3">
                          <p className="font-semibold text-[#041C3E]">{d.label} · {d.total}/30 · {levelFor(d.total)}</p>
                          <p className="text-[#3A4A63]">Explore: {map?.families}</p>
                          <p className="text-[#9CA3AF] text-xs mt-0.5">Validate by: {map?.validate}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {step.acknowledgement && (
                <div>
                  <p className="text-sm text-[#162030] leading-relaxed bg-white border border-[#E5E7EB] p-4">{ACK_TEXT}</p>
                  <button type="button" data-testid="intake-ack-checkbox" onClick={() => setAcknowledged((a) => !a)}
                    className={`flex items-center gap-3 mt-4 text-sm font-semibold w-full text-left p-4 border transition-colors ${
                      acknowledged ? "border-[#041C3E] bg-[#041C3E] text-white" : "border-[#E5E7EB] bg-white hover:border-[#041C3E]"
                    }`}>
                    <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${acknowledged ? "border-white" : "border-[#9CA3AF]"}`}>
                      {acknowledged && <CheckCircle2 size={14} />}
                    </span>
                    I have read and I accept this acknowledgment.
                  </button>
                </div>
              )}

              {(step.fields || []).map((f) => (
                <Field key={f.name} f={f} value={values[f.name]} setValue={setValue} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10">
          <button type="button" data-testid="intake-back-button" onClick={back} disabled={stepIdx === 0}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#3A4A63] hover:text-[#041C3E] disabled:opacity-30">
            <ArrowLeft size={16} /> Back
          </button>
          <button type="button" data-testid="intake-next-button" onClick={next} disabled={busy}
            className="inline-flex items-center gap-2 bg-[#D94833] text-white px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting</>
              : stepIdx === steps.length - 1 ? <><Sparkles size={16} /> Submit assessment</>
              : <>Continue <ArrowRight size={16} /></>}
          </button>
        </div>
      </main>
      <IntakeHelpAssistant />
    </div>
  );
}
